// « La poule de l’étage »: a chicken strolling between the teams of a floor (floor.chicken === true).
// Pure behaviour, no three.js import (unit-tested in Node); chicken-view.js draws it. Decoration
// only: it lives in the browser, nothing on the server, no AI call.
//
// The chicken walks straight segments that cross no pod, walkway or hall (targets are picked at
// random on the free floor area and dropped when the way is blocked), pecks the ground, sometimes
// hops with a flap of its wings, and rests still most of the time. Pacing (pacing.js): while it
// moves it asks for frames at the agents' working rate; resting, a hidden page, the building view,
// the elevator ride and reduced motion ask for none. The step functions only mutate the state
// object: nothing is allocated per frame.

import { rng } from './layout.js';
import { SCENE_PACING } from './pacing.js';
import { damp, dampAngle, wrapAngle, clamp, lerp } from './anim.js';
import { floorArea, isFree, nearTeam, segmentClear, startPoint } from './floor-area.js';

// The free floor area is shared with the floor's cat (floor-area.js).
export { isFree, nearTeam, segmentClear, startPoint };

/** Behaviour settings (world units, seconds, radians). */
export const CHICKEN = Object.freeze({
  radius: 0.36,        // keep-out around pods, walkways and the hall (half a chicken + room)
  edge: 0.9,           // how far beyond the floor's content (pods, hall) it may wander
  near: 2,             // targets stay this close to a team (a pod), so it strolls between them
  speed: 0.44,         // walking speed
  turnRate: 7,         // yaw damping toward the heading (1/s)
  stride: 0.09,        // distance per leg swing
  walkMin: 0.8,
  walkMax: 3,
  walkTimeout: 14,     // a walk that takes longer than this ends (never stuck)
  restMin: 6,
  restMax: 14,
  firstRestMin: 1.2,   // a chicken that just appeared starts moving soon
  firstRestMax: 3.5,
  retryRest: 1.5,      // short rest when no reachable target was found
  peckPeriod: 0.42,
  peckMin: 2,
  peckMax: 4,
  hopTime: 0.5,
  hopHeight: 0.2,
  hopDist: 0.4,
  settleRate: 14,      // back to the still pose before a rest
  tries: 24,           // random targets tried before giving up
  wakeSlack: 0.03,     // a rest that ends within this is over (timer jitter)
});

const NO_ENV = Object.freeze({});
const TWO_PI = Math.PI * 2;

/**
 * Floor ids whose chicken is on (only a boolean true counts).
 * @param {{floors?:Array<{id?:string, chicken?:unknown}>}} office
 * @returns {Set<string>}
 */
export function chickenFloorIds(office) {
  const ids = new Set();
  for (const f of office?.floors || []) if (f && f.id && f.chicken === true) ids.add(f.id);
  return ids;
}

/**
 * Where the chicken may walk on a floor (floor-area.js floorArea): around the floor's content kept
 * inside its slab, off every pod, walkway and the hall, each grown by `margin`.
 * @param {object} layout the floor layout (layout.js floorLayout)
 * @param {{margin?:number, edge?:number, near?:number}} [opts]
 */
export function chickenArea(layout, opts = {}) {
  return floorArea(layout, {
    margin: opts.margin ?? CHICKEN.radius, edge: opts.edge ?? CHICKEN.edge, near: opts.near ?? CHICKEN.near,
  });
}

/**
 * A new chicken, resting at a random free point (or at `at` when it is free: the floor was rebuilt).
 * @param {{area:ReturnType<typeof chickenArea>, seed?:number, now?:number,
 *   at?:{x:number, z:number, yaw?:number}|null}} opts now: wall clock (s)
 */
export function createChicken({ area, seed = 1, now = 0, at = null }) {
  const rand = rng(seed);
  let p = startPoint(area, rand);
  let yaw = rand() * TWO_PI - Math.PI;
  if (at && Number.isFinite(at.x) && Number.isFinite(at.z) && isFree(area, at.x, at.z)) {
    p = { x: at.x, z: at.z };
    if (Number.isFinite(at.yaw)) yaw = wrapAngle(at.yaw);
  }
  return {
    area, rand,
    x: p.x, z: p.z, y: 0, yaw,
    mode: 'rest',
    until: now + lerp(CHICKEN.firstRestMin, CHICKEN.firstRestMax, rand()),
    posed: true,
    // Walk (deadline: wall clock).
    tx: p.x, tz: p.z, speed: 0, gait: 0, amp: 0, deadline: 0,
    // Peck / hop progress.
    k: 0, count: 0, hx0: p.x, hz0: p.z, hx1: p.x, hz1: p.z,
    // Pose: legs swing (rad), body bob and crouch, head thrust (model units), head and body pitch,
    // wings open (rad).
    legL: 0, legR: 0, bob: 0, crouch: 0, headFwd: 0, headPitch: 0, bodyPitch: 0, wing: 0,
  };
}

/** True while the chicken moves (or settles into its still pose): it needs frames. */
export function wantsFrames(s) {
  return !!s && (s.mode !== 'rest' || !s.posed);
}

function frozen(env) {
  return !!(env.hidden || env.building || env.riding || env.reducedMotion);
}

/**
 * Frame rate the chicken asks for: the agents' working rate while it moves, 0 otherwise.
 * @param {object|null} s chicken state
 * @param {{hidden?:boolean, building?:boolean, riding?:boolean, reducedMotion?:boolean}} [env]
 * @param {typeof SCENE_PACING} [pacing]
 * @returns {number}
 */
export function chickenFps(s, env = NO_ENV, pacing = SCENE_PACING) {
  if (!s || frozen(env)) return 0;
  return wantsFrames(s) ? pacing.activeFps : 0;
}

/**
 * Wall-clock time (s) at which a resting chicken wants its next frame (it stands up); Infinity when
 * it needs no wake-up (moving: it already asks for frames; frozen: nothing to do).
 * @param {object|null} s
 * @param {object} [env] as chickenFps
 * @returns {number}
 */
export function chickenWakeAt(s, env = NO_ENV) {
  if (!s || frozen(env) || wantsFrames(s)) return Infinity;
  return s.until;
}

/**
 * Advances the chicken. `now` is the wall clock (s): rests last real seconds even when no frame was
 * drawn during them; motion uses `dt` (capped).
 * @param {object} s
 * @param {number} dt seconds
 * @param {number} now seconds
 * @param {{hidden?:boolean, building?:boolean, riding?:boolean, reducedMotion?:boolean}} [env]
 * @returns {boolean} true when the chicken needs the next frame
 */
export function stepChicken(s, dt, now, env = NO_ENV) {
  if (!s) return false;
  if (env.reducedMotion) {
    standStill(s, now);
    return false;
  }
  if (frozen(env)) return false;
  const d = clamp(Number(dt) || 0, 0, 0.2);
  if (s.mode === 'walk') walk(s, d, now);
  else if (s.mode === 'peck') peck(s, d, now);
  else if (s.mode === 'hop') hop(s, d, now);
  else rest(s, d, now);
  return wantsFrames(s);
}

/** Reduced motion: the still pose at once, on the ground; it starts again when motion is back. */
function standStill(s, now) {
  if (s.mode !== 'rest') s.until = now;
  s.mode = 'rest';
  s.posed = true;
  s.y = 0;
  s.speed = 0;
  s.amp = 0;
  s.legL = 0; s.legR = 0; s.bob = 0; s.crouch = 0;
  s.headFwd = 0; s.headPitch = 0; s.bodyPitch = 0; s.wing = 0;
}

function relaxLegs(s, d) {
  const r = CHICKEN.settleRate;
  s.amp = damp(s.amp, 0, r, d);
  s.legL = damp(s.legL, 0, r, d);
  s.legR = damp(s.legR, 0, r, d);
  s.bob = damp(s.bob, 0, r, d);
  s.headFwd = damp(s.headFwd, 0, r, d);
}

function relaxUpper(s, d) {
  const r = CHICKEN.settleRate;
  s.headPitch = damp(s.headPitch, 0, r, d);
  s.bodyPitch = damp(s.bodyPitch, 0, r, d);
  s.crouch = damp(s.crouch, 0, r, d);
  s.wing = damp(s.wing, 0, r, d);
}

function settled(s) {
  const e = 0.004;
  return Math.abs(s.legL) < e && Math.abs(s.legR) < e && Math.abs(s.bob) < e * 0.1 && Math.abs(s.headFwd) < e * 0.1
    && Math.abs(s.headPitch) < e && Math.abs(s.bodyPitch) < e && Math.abs(s.crouch) < e * 0.1
    && Math.abs(s.wing) < e && s.y === 0;
}

function rest(s, d, now) {
  if (!s.posed) {
    relaxLegs(s, d);
    relaxUpper(s, d);
    if (settled(s)) {
      s.amp = 0;
      s.legL = 0; s.legR = 0; s.bob = 0; s.headFwd = 0;
      s.headPitch = 0; s.bodyPitch = 0; s.crouch = 0; s.wing = 0;
      s.posed = true;
    }
  }
  if (now >= s.until - CHICKEN.wakeSlack) next(s, now, 'rest');
}

function startRest(s, now, short = false) {
  s.mode = 'rest';
  s.speed = 0;
  s.until = now + (short ? CHICKEN.retryRest : lerp(CHICKEN.restMin, CHICKEN.restMax, s.rand()));
  s.posed = settled(s);
}

/**
 * Picks a reachable target a few steps away near a team (ahead first, then anywhere; the last tries
 * accept any reachable point, so a chicken that strayed comes back).
 */
export function pickTarget(s) {
  const C = CHICKEN;
  for (let i = 0; i < C.tries; i++) {
    const a = i < C.tries / 2 ? s.yaw + (s.rand() - 0.5) * 2.6 : s.rand() * TWO_PI;
    const dist = lerp(C.walkMin, C.walkMax, s.rand());
    const tx = s.x + Math.sin(a) * dist;
    const tz = s.z + Math.cos(a) * dist;
    if (segmentClear(s.area, s.x, s.z, tx, tz) && (i >= C.tries - 4 || nearTeam(s.area, tx, tz))) {
      s.tx = tx;
      s.tz = tz;
      return true;
    }
  }
  return false;
}

function startWalk(s, now) {
  if (!pickTarget(s)) return false;
  s.mode = 'walk';
  s.deadline = now + CHICKEN.walkTimeout;
  return true;
}

function startPeck(s) {
  s.mode = 'peck';
  s.speed = 0;
  s.k = 0;
  s.count = CHICKEN.peckMin + Math.floor(s.rand() * (CHICKEN.peckMax - CHICKEN.peckMin + 1));
}

function startHop(s) {
  s.mode = 'hop';
  s.speed = 0;
  s.k = 0;
  s.hx0 = s.x;
  s.hz0 = s.z;
  const fx = s.x + Math.sin(s.yaw) * CHICKEN.hopDist;
  const fz = s.z + Math.cos(s.yaw) * CHICKEN.hopDist;
  // Forward when the landing is free, else straight up.
  const clear = segmentClear(s.area, s.x, s.z, fx, fz);
  s.hx1 = clear ? fx : s.x;
  s.hz1 = clear ? fz : s.z;
}

/** What comes after `from`: mostly a rest. */
function next(s, now, from) {
  const r = s.rand();
  if (from === 'rest') {
    if (r < 0.62) {
      if (!startWalk(s, now)) startRest(s, now, true);
    } else if (r < 0.84) startPeck(s);
    else startHop(s);
  } else if (from === 'walk') {
    if (r < 0.4) startPeck(s);
    else if (r < 0.5) startHop(s);
    else startRest(s, now);
  } else if (from === 'peck') {
    if (!(r < 0.2 && startWalk(s, now))) startRest(s, now);
  } else if (r < 0.35) startPeck(s);
  else startRest(s, now);
}

function walk(s, d, now) {
  const C = CHICKEN;
  relaxUpper(s, d);
  const dx = s.tx - s.x;
  const dz = s.tz - s.z;
  const dist = Math.hypot(dx, dz);
  if (dist < 0.02 || now > s.deadline) {
    next(s, now, 'walk');
    return;
  }
  const heading = Math.atan2(dx, dz);
  s.yaw = wrapAngle(dampAngle(s.yaw, heading, C.turnRate, d));
  const off = Math.abs(wrapAngle(heading - s.yaw));
  // Turns on the spot first, then walks; slows down on arrival.
  const align = clamp(1 - off / 0.9, 0, 1);
  const brake = clamp(dist / 0.35, 0.3, 1);
  s.speed = damp(s.speed, C.speed * align * brake, 6, d);
  // Along the checked segment (not along the yaw): the path never leaves the free area.
  const step = Math.min(dist, s.speed * d);
  s.x += (dx / dist) * step;
  s.z += (dz / dist) * step;
  // Legs swing with the distance covered, and step on the spot while turning.
  s.gait = (s.gait + (step / C.stride) * Math.PI + (1 - align) * d * 9) % TWO_PI;
  s.amp = damp(s.amp, clamp((s.speed / C.speed) * 1.3 + (1 - align), 0, 1), 10, d);
  const swing = Math.sin(s.gait);
  s.legL = swing * 0.55 * s.amp;
  s.legR = -s.legL;
  s.bob = Math.abs(swing) * 0.012 * s.amp;
  // The head bobs once per step.
  s.headFwd = Math.sin(s.gait * 2) * 0.028 * s.amp;
}

function peck(s, d, now) {
  const C = CHICKEN;
  relaxLegs(s, d);
  s.wing = damp(s.wing, 0, C.settleRate, d);
  s.k += d;
  const n = Math.floor(s.k / C.peckPeriod);
  if (n >= s.count) {
    s.headPitch = 0; s.bodyPitch = 0; s.crouch = 0;
    next(s, now, 'peck');
    return;
  }
  const f = s.k / C.peckPeriod - n;
  // Down to the ground and back up quickly, then a short pause.
  const down = f < 0.55 ? Math.sin((Math.PI * f) / 0.55) : 0;
  s.headPitch = 1.5 * down;
  s.bodyPitch = 0.7 * down;
  s.crouch = 0.04 * down;
}

function hop(s, d, now) {
  const C = CHICKEN;
  s.headPitch = damp(s.headPitch, 0, C.settleRate, d);
  s.bodyPitch = damp(s.bodyPitch, 0, C.settleRate, d);
  s.bob = damp(s.bob, 0, C.settleRate, d);
  s.headFwd = damp(s.headFwd, 0, C.settleRate, d);
  s.amp = 0;
  s.k += d / C.hopTime;
  const p = Math.min(1, s.k);
  // A short crouch, then a parabola (legs tucked, wings flapping).
  const crouchEnd = 0.2;
  if (p < crouchEnd) {
    s.crouch = 0.05 * Math.sin((Math.PI * p) / crouchEnd);
    s.y = 0;
    s.legL = damp(s.legL, 0, C.settleRate, d);
    s.legR = s.legL;
    s.wing = damp(s.wing, 0.5, 10, d);
  } else {
    const q = (p - crouchEnd) / (1 - crouchEnd);
    s.crouch = 0;
    s.y = 4 * C.hopHeight * q * (1 - q);
    s.x = lerp(s.hx0, s.hx1, q);
    s.z = lerp(s.hz0, s.hz1, q);
    s.legL = 0.45 * Math.sin(Math.PI * q); // feet tucked back
    s.legR = s.legL;
    s.wing = 0.35 + 0.75 * Math.abs(Math.sin(q * Math.PI * 3));
  }
  if (p >= 1) {
    s.y = 0;
    s.x = s.hx1;
    s.z = s.hz1;
    next(s, now, 'hop');
  }
}
